import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Col, DatePicker, Empty, Flex, Row, Segmented, Space, Statistic, Table, Tabs, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type ChannelPlatformName } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { BarChart } from "@/components/BarChart";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { PlatformLabel } from "./channels/PlatformBadge";

const DAY = "YYYY-MM-DD";
const TABS = ["sales", "fulfillment", "products"] as const;
type Tab = (typeof TABS)[number];

/**
 * Analitik: sales and order fulfilment over a period (by the day orders were
 * placed). Staff performance has its own screen under Gudang · WMS; finance
 * reports are under Keuangan.
 */
export function Analytics() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useUrlFilter<Tab>("tab", "sales", TABS);
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(29, "day"), dayjs()]);
  const query = { from: range[0].format(DAY), to: range[1].format(DAY) };
  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, fontSize: 28 }}>
        {t("nav.analytics")}
      </Typography.Title>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 8 }}>
        <DatePicker.RangePicker value={range} allowClear={false} onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])} format="DD/MM/YYYY" />
        <div style={{ flex: 1 }} />
        {can("warehouse.operate") && <Button onClick={() => navigate("/app/warehouse/performance")}>{t("an.staffLink")}</Button>}
      </Flex>
      <Insights onTab={setTab} />
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as Tab)}
        items={[
          { key: "sales", label: t("an.tab.sales"), children: <Sales query={query} /> },
          { key: "fulfillment", label: t("an.tab.fulfillment"), children: <Fulfillment query={query} /> },
          { key: "products", label: t("an.tab.products"), children: <Products query={query} /> },
        ]}
      />
    </>
  );
}

type Query = { from: string; to: string };

function Tile({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <Col xs={12} md={8} xl={4}>
      <Card size="small">
        <Statistic title={title} value={value} />
        {hint && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {hint}
          </Typography.Text>
        )}
      </Card>
    </Col>
  );
}

function Sales({ query }: { query: Query }) {
  const { t, formatMoney, formatNumber } = useI18n();
  const [measure, setMeasure] = useState<"revenue" | "orders">("revenue");
  const [view, setView] = useState<"chart" | "table">("chart");
  const q = useQuery({
    queryKey: ["analytics", "sales", query],
    queryFn: () => unwrap(api.GET("/v1/analytics/sales", { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  if (!d) return q.error ? <Alert type="error" title={String(q.error)} /> : <Card loading />;
  const s = d.totals;
  const fmt = measure === "revenue" ? formatMoney : formatNumber;
  return (
    <Flex vertical gap={16}>
      <Row gutter={[12, 12]}>
        <Tile title={t("an.revenue")} value={formatMoney(s.revenue)} hint={t("an.revenueHint")} />
        <Tile title={t("an.orders")} value={formatNumber(s.orders)} />
        <Tile title={t("an.units")} value={formatNumber(s.units)} />
        <Tile title={t("an.avgOrder")} value={formatMoney(d.avgOrder)} />
        <Tile title={t("an.cancelled")} value={formatNumber(s.cancelled)} hint={pct(s.cancelled, s.orders + s.cancelled)} />
        <Tile title={t("an.returned")} value={formatNumber(s.returned)} hint={pct(s.returned, s.orders)} />
      </Row>
      <Card
        size="small"
        title={t(measure === "revenue" ? "an.revenuePerDay" : "an.ordersPerDay")}
        extra={
          <Flex gap={8}>
            <Segmented size="small" value={measure} onChange={(v) => setMeasure(v as typeof measure)} options={[{ value: "revenue", label: t("an.revenue") }, { value: "orders", label: t("an.orders") }]} />
            <Segmented size="small" value={view} onChange={(v) => setView(v as typeof view)} options={[{ value: "chart", label: t("an.chart") }, { value: "table", label: t("an.table") }]} />
          </Flex>
        }
      >
        {d.days.length === 0 ? (
          <Empty description={t("an.empty")} />
        ) : view === "chart" ? (
          <BarChart
            ariaLabel={t(measure === "revenue" ? "an.revenuePerDay" : "an.ordersPerDay")}
            format={fmt}
            bars={d.days.map((x) => ({
              key: x.day,
              label: dayjs(x.day).format("DD/MM"),
              value: x[measure],
              tip: `${dayjs(x.day).format("ddd DD MMM")}: ${formatNumber(x.orders)} ${t("an.orders").toLowerCase()} · ${formatMoney(x.revenue)}`,
            }))}
          />
        ) : (
          <Table
            size="small"
            rowKey="day"
            pagination={false}
            dataSource={d.days}
            scroll={{ y: 320 }}
            columns={[
              { title: t("an.day"), dataIndex: "day", render: (v: string) => dayjs(v).format("ddd DD/MM/YYYY") },
              { title: t("an.orders"), dataIndex: "orders", align: "right", render: formatNumber },
              { title: t("an.units"), dataIndex: "units", align: "right", render: formatNumber },
              { title: t("an.revenue"), dataIndex: "revenue", align: "right", render: (v: number) => formatMoney(v) },
              { title: t("an.cancelled"), dataIndex: "cancelled", align: "right", render: formatNumber },
              { title: t("an.returned"), dataIndex: "returned", align: "right", render: formatNumber },
            ]}
          />
        )}
      </Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card size="small" title={t("an.byStore")}>
            <Table
              size="small"
              rowKey="connectionId"
              pagination={false}
              dataSource={d.stores}
              locale={{ emptyText: t("an.empty") }}
              columns={[
                { title: t("an.store"), dataIndex: "name", render: (v: string, r) => (
                    <Space orientation="vertical" size={0}>
                      <span>{v}</span>
                      <PlatformLabel platform={r.platform as ChannelPlatformName} />
                    </Space>
                  ),
                },
                { title: t("an.orders"), dataIndex: "orders", align: "right", render: formatNumber },
                { title: t("an.revenue"), dataIndex: "revenue", align: "right", render: (v: number) => formatMoney(v) },
                { title: t("an.cancelled"), dataIndex: "cancelled", align: "right", render: formatNumber },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card size="small" title={t("an.topSkus")}>
            <Table
              size="small"
              rowKey="skuId"
              pagination={false}
              dataSource={d.topSkus}
              locale={{ emptyText: t("an.empty") }}
              columns={[
                { title: t("an.sku"), dataIndex: "code", render: (v: string, r) => <span title={r.name} className="z-num">{v}</span> },
                { title: t("an.name"), dataIndex: "name", ellipsis: true },
                { title: t("an.units"), dataIndex: "units", align: "right", render: formatNumber },
                { title: t("an.revenue"), dataIndex: "revenue", align: "right", render: (v: number) => formatMoney(v) },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </Flex>
  );
}

function pct(n: number, of: number) {
  return of > 0 ? `${((n / of) * 100).toFixed(1)}%` : undefined;
}

function Fulfillment({ query }: { query: Query }) {
  const { t, tDynamic, formatNumber } = useI18n();
  const q = useQuery({
    queryKey: ["analytics", "fulfillment", query],
    queryFn: () => unwrap(api.GET("/v1/analytics/fulfillment", { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  if (!d) return q.error ? <Alert type="error" title={String(q.error)} /> : <Card loading />;
  const s = d.totals;
  const hours = (h: number) => (h > 0 ? t("an.hours", { n: h < 10 ? h.toFixed(1) : Math.round(h).toString() }) : "—");
  return (
    <Flex vertical gap={16}>
      <Row gutter={[12, 12]}>
        <Tile title={t("an.orders")} value={formatNumber(s.orders)} />
        <Tile title={t("an.packed")} value={formatNumber(s.packed)} hint={pct(s.packed, s.orders)} />
        <Tile title={t("an.handed")} value={formatNumber(s.handed)} hint={pct(s.handed, s.orders)} />
        <Tile title={t("an.late")} value={formatNumber(s.late)} hint={pct(s.late, s.handed)} />
        <Tile title={t("an.overdue")} value={formatNumber(s.overdue)} hint={t("an.overdueHint")} />
        <Tile title={t("an.toHanded")} value={hours(s.toHandedHours)} hint={`${t("an.toPacked")} ${hours(s.toPackedHours)} · ${t("an.packedToHanded")} ${hours(s.packedToHandedHours)}`} />
      </Row>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t("an.fulfillmentHelp")}
      </Typography.Text>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card size="small" title={t("an.perDay")}>
            <Table
              size="small"
              rowKey="day"
              pagination={false}
              dataSource={d.days}
              scroll={{ y: 360 }}
              locale={{ emptyText: t("an.empty") }}
              columns={[
                { title: t("an.day"), dataIndex: "day", render: (v: string) => dayjs(v).format("ddd DD/MM") },
                { title: t("an.orders"), dataIndex: "orders", align: "right", render: formatNumber },
                { title: t("an.packed"), dataIndex: "packed", align: "right", render: formatNumber },
                { title: t("an.handed"), dataIndex: "handed", align: "right", render: formatNumber },
                { title: t("an.late"), dataIndex: "late", align: "right", render: (v: number) => (v > 0 ? <Tag color="warning">{formatNumber(v)}</Tag> : "—") },
                { title: t("an.overdue"), dataIndex: "overdue", align: "right", render: (v: number) => (v > 0 ? <Tag color="error">{formatNumber(v)}</Tag> : "—") },
                { title: t("an.toHanded"), dataIndex: "toHandedHours", align: "right", render: hours },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card size="small" title={t("an.heldNow")}>
            <Table
              size="small"
              rowKey="hold"
              pagination={false}
              dataSource={d.held}
              locale={{ emptyText: t("an.noHeld") }}
              columns={[
                { title: t("an.reason"), dataIndex: "hold", render: (v: string) => tDynamic(`orders.hold.${v}`, "orders.holdLabel") },
                { title: t("an.orders"), dataIndex: "orders", align: "right", render: formatNumber },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </Flex>
  );
}

type Insight = Awaited<ReturnType<typeof fetchInsights>>["insights"][number];
const fetchInsights = () => unwrap(api.GET("/v1/analytics/insights"));
const SEVERITY = { critical: "error", warning: "warning", good: "success", info: "info" } as const;

/**
 * Insight: what the rules on the server found worth a look in the last weeks,
 * most urgent first, each saying why and where to go.
 */
function Insights({ onTab }: { onTab: (t: Tab) => void }) {
  const { t, tDynamic, formatMoney, formatNumber } = useI18n();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["analytics", "insights"], queryFn: fetchInsights, staleTime: 5 * 60_000 });
  const items = q.data?.insights ?? [];
  if (q.isPending) return null;
  const vars = (x: Insight): Record<string, string | number> => {
    const p = x.params as Record<string, string | number>;
    const v: Record<string, string | number> = {};
    for (const [k, val] of Object.entries(p)) {
      v[k] = typeof val === "number" ? (k === "revenue" || k === "previous" ? formatMoney(val) : formatNumber(val)) : val;
    }
    if (typeof p.day === "string") v.day = dayjs(p.day).format("ddd DD MMM");
    if (typeof p.reason === "string") v.reason = tDynamic(`orders.hold.${p.reason}`, "orders.holdLabel");
    return v;
  };
  const action = (x: Insight) => {
    switch (x.kind) {
      case "stock_out":
      case "stock_low":
      case "stock_dead":
        return { label: t("an.ins.seeProducts"), go: () => onTab("products") };
      case "orders_held":
      case "orders_overdue":
        return { label: t("an.ins.seeOrders"), go: () => navigate("/app/orders") };
      case "shipping_late":
        return { label: t("an.ins.seeFulfillment"), go: () => onTab("fulfillment") };
      default:
        return null;
    }
  };
  return (
    <Card size="small" title={t("an.ins.title")} style={{ marginBottom: 16 }} extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("an.ins.help")}</Typography.Text>}>
      {items.length === 0 ? (
        <Typography.Text type="secondary">{t("an.ins.none")}</Typography.Text>
      ) : (
        <Flex vertical gap={8}>
          {items.map((x, i) => {
            const a = action(x);
            return (
              <Alert
                key={i}
                showIcon
                type={SEVERITY[x.severity]}
                title={tDynamic(`an.ins.${x.kind}`, "an.ins.title", vars(x))}
                action={a ? <Button size="small" onClick={a.go}>{a.label}</Button> : undefined}
              />
            );
          })}
        </Flex>
      )}
    </Card>
  );
}

const CLASS_COLOR = { A: "green", B: "blue", C: "default" } as const;

function Products({ query }: { query: Query }) {
  const { t, formatMoney, formatNumber } = useI18n();
  const q = useQuery({
    queryKey: ["analytics", "products", query],
    queryFn: () => unwrap(api.GET("/v1/analytics/products", { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  if (!d) return q.error ? <Alert type="error" title={String(q.error)} /> : <Card loading />;
  return (
    <Flex vertical gap={16}>
      <Row gutter={[12, 12]}>
        {(["A", "B", "C"] as const).map((c) => (
          <Tile
            key={c}
            title={t(`an.class${c}`)}
            value={t("an.skus", { n: formatNumber(d.classes[c].skus) })}
            hint={t("an.ofRevenue", { pct: d.classes[c].share.toFixed(1) })}
          />
        ))}
        <Tile title={t("an.runningOut")} value={t("an.skus", { n: formatNumber(d.runningOut.length) })} hint={t("an.runningOutHint")} />
        <Tile title={t("an.dead")} value={t("an.skus", { n: formatNumber(d.dead.skus) })} hint={t("an.deadHint", { units: formatNumber(d.dead.units) })} />
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={13}>
          <Card size="small" title={t("an.pareto")} extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("an.paretoHelp")}</Typography.Text>}>
            <Table
              size="small"
              rowKey="skuId"
              dataSource={d.pareto}
              pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{ emptyText: t("an.empty") }}
              columns={[
                { title: "", dataIndex: "class", width: 44, render: (v: "A" | "B" | "C") => <Tag color={CLASS_COLOR[v]}>{v}</Tag> },
                { title: t("an.sku"), dataIndex: "code", render: (v: string, r) => <span className="z-num" title={r.name}>{v}</span> },
                { title: t("an.units"), dataIndex: "units", align: "right", render: formatNumber },
                { title: t("an.revenue"), dataIndex: "revenue", align: "right", render: (v: number) => formatMoney(v) },
                { title: t("an.share"), dataIndex: "share", align: "right", render: (v: number) => `${v.toFixed(1)}%` },
                { title: t("an.cumShare"), dataIndex: "cumShare", align: "right", render: (v: number) => `${v.toFixed(1)}%` },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={11}>
          <Card size="small" title={t("an.runningOut")} extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("an.runningOutHelp")}</Typography.Text>}>
            <Table
              size="small"
              rowKey="skuId"
              dataSource={d.runningOut}
              pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{ emptyText: t("an.noRunningOut") }}
              columns={[
                { title: t("an.sku"), dataIndex: "code", render: (v: string, r) => <span className="z-num" title={r.name}>{v}</span> },
                { title: t("an.available"), dataIndex: "available", align: "right", render: formatNumber },
                { title: t("an.soldPerDay"), dataIndex: "perDay", align: "right" },
                {
                  title: t("an.cover"),
                  dataIndex: "coverDays",
                  align: "right",
                  render: (v: number, r) =>
                    r.available <= 0 ? <Tag color="error">{t("an.empty0")}</Tag> : <Tag color={v < 7 ? "warning" : "default"}>{t("an.days", { n: v.toFixed(1) })}</Tag>,
                },
                { title: t("an.incoming"), dataIndex: "incoming", align: "right", render: (v: number) => (v ? formatNumber(v) : "—") },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </Flex>
  );
}
